import { Button, Group, Image, Loader, Modal, SimpleGrid, Stack, Text, UnstyledButton } from "@mantine/core";
import { ArrowLeft } from "lucide-react";
import { useState, type ReactNode } from "react";

import { useListProducts } from "../../../api/generated/endpoints/teams/teams";
import type { Job, Product } from "../../../api/generated/model";
import { ErrorAlert } from "../../../platform/ErrorAlert";
import { deliveredOutputs } from "../../../platform/jobs/output-picks";
import { useJobList } from "../../../platform/jobs/job-list";
import type { PickerLibrary } from "../../../platform/media/ImagePicker";
import { ReferencesDialog } from "../../../platform/media/ReferencesDialog";
import { previewKind } from "../../../platform/media/output-kind";
import { jobMedium } from "../../../platform/jobs/detail/outputs";
import type { PictureChoice } from "./picture";
import classes from "./layer-editor.module.css";

/** Live's three picture sources of "Add layer". */
export type PictureSource = "product" | "generation" | "library";

export interface LayerPickersProps {
  library: PickerLibrary;
  /** The picker open, or none. */
  open: PictureSource | null;
  onClose: () => void;
  onChoose: (choice: PictureChoice) => void;
}

/** The picker open of live's three: "Product images", "My Generations", "Library or upload". */
export function LayerPickers({ library, open, onClose, onChoose }: LayerPickersProps) {
  if (open === "product") return <ProductPicker library={library} onClose={onClose} onChoose={onChoose} />;
  if (open === "generation") {
    return <GenerationPicker library={library} onClose={onClose} onChoose={onChoose} />;
  }
  if (open === "library") {
    return (
      <ReferencesDialog
        teamId={library.teamId}
        brandId={library.brandId}
        title="Choose Library image"
        subtitle="Choose from Library or upload a new image"
        max={1}
        chosen={[]}
        onClose={onClose}
        onConfirm={([picked]) => {
          const url = picked?.original ?? picked?.url ?? null;
          if (picked === undefined || url === null) return;
          onChoose({
            ref: { kind: "asset", asset_id: picked.assetId },
            source: "library",
            url,
            // Live names every Library layer so.
            name: "Library image",
            width: picked.width,
            height: picked.height,
          });
        }}
      />
    );
  }
  return null;
}

interface PickerProps {
  library: PickerLibrary;
  onClose: () => void;
  onChoose: (choice: PictureChoice) => void;
}

function Dialog(props: { title: string; subtitle: string; size: number; onClose: () => void; children: ReactNode }) {
  return (
    <Modal
      opened
      onClose={props.onClose}
      size={props.size}
      centered
      title={
        <Stack gap={2}>
          <Text fw={600}>{props.title}</Text>
          <Text size="sm" c="var(--cai-color-text-muted)">
            {props.subtitle}
          </Text>
        </Stack>
      }
    >
      {props.children}
    </Modal>
  );
}

/** A picture to choose: its preview and the lines under it. */
function Choice(props: { label: string; url: string; children: ReactNode; onClick: () => void; onSize?: (size: string) => void }) {
  return (
    <UnstyledButton className={classes.choice} aria-label={props.label} onClick={props.onClick}>
      <Image
        src={props.url}
        alt=""
        fit="contain"
        loading="lazy"
        className={classes.choicePicture}
        onLoad={(event) => {
          const { naturalWidth, naturalHeight } = event.currentTarget;
          if (naturalWidth > 0 && naturalHeight > 0) props.onSize?.(`${naturalWidth} × ${naturalHeight}`);
        }}
      />
      <span className={classes.choiceText}>{props.children}</span>
    </UnstyledButton>
  );
}

/** A product's picture in the list: its main photo, else its first with a picture. */
function coverOf(product: Product): string {
  const shown = product.images.filter((image) => image.url !== null);
  return (shown.find((image) => image.is_main === true) ?? shown[0])!.url!;
}

/** Live's "Product images": a product, then the exact photo of it. */
function ProductPicker({ library, onClose, onChoose }: PickerProps) {
  const [product, setProduct] = useState<Product | null>(null);
  const products = useListProducts(library.teamId, library.brandId);
  const withPhotos = (products.data ?? []).filter((each) => each.images.some((image) => image.url !== null));
  return (
    <Dialog
      title={product === null ? "Choose product" : "Choose product image"}
      subtitle="Select the exact product photo to use as a layer."
      size={760}
      onClose={onClose}
    >
      {product === null ? (
        <Stack gap="md">
          {products.isPending ? <Loader size="sm" aria-label="Loading products" /> : null}
          {products.isError ? <ErrorAlert error={products.error} /> : null}
          <SimpleGrid cols={{ base: 2, sm: 3 }} spacing="sm">
            {withPhotos.map((each) => (
              <Choice
                key={each.id}
                label={each.name}
                url={coverOf(each)}
                onClick={() => setProduct(each)}
              >
                <Text size="xs" fw={500} lineClamp={2}>
                  {each.name}
                </Text>
              </Choice>
            ))}
          </SimpleGrid>
        </Stack>
      ) : (
        <ProductPhotos product={product} onBack={() => setProduct(null)} onChoose={onChoose} />
      )}
    </Dialog>
  );
}

function ProductPhotos(props: { product: Product; onBack: () => void; onChoose: (choice: PictureChoice) => void }) {
  const { product, onBack, onChoose } = props;
  const [sizes, setSizes] = useState<Record<string, string>>({});
  const photos = product.images.filter((image) => image.url !== null);
  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Button variant="subtle" size="compact-sm" leftSection={<ArrowLeft size={15} />} onClick={onBack}>
          Products
        </Button>
        <Text size="sm" fw={600}>
          {product.name}
        </Text>
      </Group>
      <SimpleGrid cols={{ base: 2, sm: 3 }} spacing="sm">
        {photos.map((photo, at) => (
          <Choice
            key={photo.asset_id}
            // Live's "Use {name}"; a product's photos here have no names of their own.
            label={`Use ${product.name}, photo ${at + 1}`}
            url={photo.url!}
            onSize={(size) => setSizes((current) => ({ ...current, [photo.asset_id]: size }))}
            onClick={() =>
              onChoose({ ref: { kind: "asset", asset_id: photo.asset_id }, source: "product", url: photo.url!, name: product.name })
            }
          >
            <Text size="xs" fw={500} lineClamp={1}>
              {product.name}
            </Text>
            <Text size="xs" c="var(--cai-color-text-muted)">
              {sizes[photo.asset_id] ?? " "}
            </Text>
          </Choice>
        ))}
      </SimpleGrid>
    </Stack>
  );
}

/** A generation's name as a layer: its prompt, else live's "Generated image". */
function generationName(job: Job): string {
  return job.params?.prompt?.trim() || "Generated image";
}

/** Live's "My Generations": the brand's delivered images, newest first, a page at a time. */
function GenerationPicker({ library, onClose, onChoose }: PickerProps) {
  const jobs = useJobList(library.teamId, { status: "all", brandId: library.brandId }, undefined);
  const choices = (jobs.data?.pages ?? [])
    .flatMap((page) => page.items)
    .filter((job) => job.status === "succeeded" && jobMedium(job) === "image")
    .flatMap((job) =>
      deliveredOutputs(job)
        .filter((output) => previewKind(output) === "image")
        .map((output) => ({ job, output })),
    );
  return (
    <Dialog
      title="Choose from My Generations"
      subtitle="Select an existing completed image to add as a layer."
      size={860}
      onClose={onClose}
    >
      <Stack gap="md">
        {jobs.isPending ? <Loader size="sm" aria-label="Loading generations" /> : null}
        {jobs.isError ? <ErrorAlert error={jobs.error} /> : null}
        {jobs.isSuccess && choices.length === 0 && !jobs.hasNextPage ? (
          <Text size="sm" c="var(--cai-color-text-muted)" ta="center">
            No completed image generations yet.
          </Text>
        ) : null}
        <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }} spacing="sm">
          {choices.map(({ job, output }, at) => (
            <Choice
              key={`${job.id}:${output.index}`}
              label={`Use generated image ${at + 1}`}
              url={output.url!}
              onClick={() =>
                onChoose({
                  ref: { kind: "job_output", job_id: job.id, output_index: output.index },
                  source: "generation",
                  url: output.url!,
                  name: generationName(job),
                  width: output.width,
                  height: output.height,
                })
              }
            >
              <Text size="xs" fw={500} lineClamp={1}>
                {generationName(job)}
              </Text>
            </Choice>
          ))}
        </SimpleGrid>
        {jobs.hasNextPage ? (
          <Button variant="default" loading={jobs.isFetchingNextPage} onClick={() => void jobs.fetchNextPage()}>
            Load more
          </Button>
        ) : null}
      </Stack>
    </Dialog>
  );
}
